"use client";

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { reportsApi } from "@/lib/api/services/reports.api";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SkeletonArticle } from "@/components/ui/skeleton";
import { FileText, Download, ArrowRight, AlertCircle } from "lucide-react";

interface Whitepaper {
  id: string;
  title: string;
  downloads: number;
  pages: number;
  category: string;
}

export default function WhitepapersPage() {
  const { data: items, isLoading, error } = useQuery({
    queryKey: ["whitepapers-list"],
    queryFn: async () => {
      const data = await reportsApi.list({ page: 1, perPage: 8, sort: "downloads", order: "desc" });
      return ((data as unknown as { data?: Whitepaper[] }).data ?? []) as Whitepaper[];
    },
  });

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-5 py-20 text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-destructive/50" />
        <h2 className="mt-4 font-heading text-xl font-semibold text-foreground">Failed to load whitepapers</h2>
        <p className="mt-2 text-sm text-muted-foreground">Please try again later.</p>
      </div>
    );
  }

  return (
    <main className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
      <div className="text-center mb-12">
        <div className="eyebrow flex items-center justify-center gap-2 mb-4">
          <FileText className="h-3.5 w-3.5 text-primary" /> Whitepapers
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-foreground">
          Research Whitepapers
        </h1>
        <p className="mt-4 text-muted-foreground leading-relaxed max-w-2xl mx-auto">
          Download free whitepapers on HR trends and best practices.
        </p>
      </div>

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {isLoading ? (
          [1, 2, 3, 4].map((i) => <SkeletonArticle key={i} />)
        ) : !items || items.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <FileText className="mx-auto h-12 w-12 text-muted-foreground/30" />
            <h3 className="mt-4 font-heading text-lg font-semibold text-foreground">No whitepapers yet</h3>
            <p className="mt-2 text-sm text-muted-foreground">Check back later for new whitepapers.</p>
          </div>
        ) : (
          items.map((wp: Whitepaper) => (
            <Card key={wp.id} className="group hover:border-primary/30 transition-all duration-200">
              <CardContent className="p-5">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                  <FileText className="h-5 w-5" />
                </div>
                <Badge variant="secondary" className="mb-3 text-[0.625rem]">{wp.category}</Badge>
                <h3 className="text-sm font-heading font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                  {wp.title}
                </h3>
                <div className="mt-3 text-xs text-muted-foreground">
                  {(wp.downloads ?? 0).toLocaleString()} downloads · {wp.pages ?? 0} pages
                </div>
                <Button variant="ghost" size="sm" asChild className="mt-4 p-0 h-auto text-xs gap-1">
                  <Link href={`/whitepapers`}>
                    Download <ArrowRight className="h-3 w-3" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </main>
  );
}
